import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { Toast } from 'primereact/toast';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Badge } from 'primereact/badge';
import { useNavigate } from 'react-router-dom';
import { MagacinProduct, MagacinProductType, MagacinReceivingIssueType } from '../types';
import { magacinProductTypesAPI, magacinProductsAPI, magacinReceivingsAPI } from '../services/api';
import './Magacin.css';

const useIsMobile = (breakpoint: number = 768) => {
  const [isMobile, setIsMobile] = useState<boolean>(
    typeof window !== 'undefined' ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
};

const unitOptions = [
  { label: 'kg', value: 'kg' },
  { label: 'kom', value: 'kom' },
  { label: 'm', value: 'm' },
  { label: 'l', value: 'l' }
];

const issueTypeOptions: Array<{ label: string; value: MagacinReceivingIssueType }> = [
  { label: 'Manjak', value: 'missing' },
  { label: 'Oštećenje', value: 'damaged' },
  { label: 'Pogrešno', value: 'wrong' },
  { label: 'Ostalo', value: 'other' }
];

const issueTypeLabel = (t: MagacinReceivingIssueType) => {
  switch (t) {
    case 'missing':
      return 'Manjak';
    case 'damaged':
      return 'Oštećenje';
    case 'wrong':
      return 'Pogrešno';
    default:
      return 'Ostalo';
  }
};

const issueTypeSeverity = (
  t: MagacinReceivingIssueType
): 'warning' | 'danger' | 'info' | 'success' => {
  switch (t) {
    case 'missing':
      return 'warning';
    case 'damaged':
      return 'danger';
    case 'wrong':
      return 'info';
    default:
      return 'success';
  }
};

const fmtDate = (d: Date | string | null | undefined) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('sr-RS');
  } catch {
    return '-';
  }
};

interface DraftItem {
  productTypeId: string | null;
  productName: string;
  quantity: number;
  unit: string;
  arrivalDate: Date;
  supplier: string;
  batchNumber: string;
  notes: string;
}

interface SavedItem extends DraftItem {
  key: string;
  categoryName: string;
}

interface DraftIssue {
  type: MagacinReceivingIssueType;
  productName: string;
  quantity: number;
  unit: string;
  description: string;
}

interface SavedIssue extends DraftIssue {
  key: string;
}

const emptyDraftItem = (): DraftItem => ({
  productTypeId: null,
  productName: '',
  quantity: 0,
  unit: 'kg',
  arrivalDate: new Date(),
  supplier: '',
  batchNumber: '',
  notes: ''
});

const emptyDraftIssue = (): DraftIssue => ({
  type: 'damaged',
  productName: '',
  quantity: 0,
  unit: '',
  description: ''
});

const MagacinReceivingCreate: React.FC = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(false);
  const [productTypes, setProductTypes] = useState<MagacinProductType[]>([]);
  const [products, setProducts] = useState<MagacinProduct[]>([]);

  const [deliveryNoteName, setDeliveryNoteName] = useState('');
  const [notes, setNotes] = useState('');

  const [items, setItems] = useState<SavedItem[]>([]);
  const [issues, setIssues] = useState<SavedIssue[]>([]);

  const [itemDraft, setItemDraft] = useState<DraftItem>(emptyDraftItem());
  const [productSuggestions, setProductSuggestions] = useState<MagacinProduct[]>([]);

  const [issueDraft, setIssueDraft] = useState<DraftIssue>(emptyDraftIssue());

  const [activeAccordion, setActiveAccordion] = useState<number | number[] | null>(
    typeof window !== 'undefined' && window.innerWidth <= 768 ? 0 : [0, 1, 2]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [types, prods] = await Promise.all([magacinProductTypesAPI.getAll(), magacinProductsAPI.getAll()]);
      setProductTypes(types);
      setProducts(prods);
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri učitavanju podataka',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const categoryOptions = useMemo(
    () => productTypes.map((t) => ({ label: t.name, value: t._id })),
    [productTypes]
  );

  const onCategoryChange = (productTypeId: string | null) => {
    const t = productTypes.find((x) => x._id === productTypeId);
    setItemDraft((prev) => ({
      ...prev,
      productTypeId,
      unit: t?.unit || prev.unit || 'kg'
    }));
  };

  const completeProduct = (e: AutoCompleteCompleteEvent) => {
    const q = (e.query || '').toLowerCase().trim();
    const filtered = q
      ? products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 10)
      : products.slice(0, 10);
    setProductSuggestions(filtered);
  };

  const validateItemDraft = (): string | null => {
    if (!itemDraft.productTypeId) return 'Odaberite kategoriju';
    if (!itemDraft.productName.trim()) return 'Unesite naziv proizvoda';
    if (!itemDraft.quantity || itemDraft.quantity <= 0) return 'Količina mora biti veća od 0';
    if (!itemDraft.arrivalDate) return 'Odaberite datum dolaska';
    return null;
  };

  const addItemToList = () => {
    const err = validateItemDraft();
    if (err) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija stavke', detail: err, life: 3000 });
      return;
    }
    const t = productTypes.find((x) => x._id === itemDraft.productTypeId);
    setItems((prev) => [
      ...prev,
      {
        ...itemDraft,
        productName: itemDraft.productName.trim(),
        supplier: itemDraft.supplier.trim(),
        batchNumber: itemDraft.batchNumber.trim(),
        notes: itemDraft.notes.trim(),
        categoryName: t?.name || '',
        key: Math.random().toString(36).slice(2)
      }
    ]);
    setItemDraft(emptyDraftItem());
    setProductSuggestions([]);
  };

  const removeItemFromList = (key: string) => {
    setItems((prev) => prev.filter((it) => it.key !== key));
  };

  const validateIssueDraft = (): string | null => {
    if (!issueDraft.description.trim()) return 'Opis je obavezan';
    return null;
  };

  const addIssueToList = () => {
    const err = validateIssueDraft();
    if (err) {
      toast.current?.show({ severity: 'warn', summary: 'Validacija izveštaja', detail: err, life: 3000 });
      return;
    }
    setIssues((prev) => [
      ...prev,
      {
        ...issueDraft,
        productName: issueDraft.productName.trim(),
        unit: issueDraft.unit.trim(),
        description: issueDraft.description.trim(),
        key: Math.random().toString(36).slice(2)
      }
    ]);
    setIssueDraft(emptyDraftIssue());
  };

  const removeIssueFromList = (key: string) => {
    setIssues((prev) => prev.filter((iss) => iss.key !== key));
  };

  const canSubmit = useMemo(() => {
    if (!deliveryNoteName.trim()) return false;
    if (items.length === 0) return false;
    return true;
  }, [deliveryNoteName, items]);

  const submit = async () => {
    if (!canSubmit) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validacija',
        detail: 'Unesite naziv otpremnice i dodajte bar jednu stavku',
        life: 3500
      });
      return;
    }
    setLoading(true);
    try {
      await magacinReceivingsAPI.create({
        deliveryNoteName: deliveryNoteName.trim(),
        notes: notes.trim(),
        items: items.map((it) => ({
          productTypeId: it.productTypeId as string,
          productName: it.productName,
          quantity: it.quantity,
          unit: it.unit,
          arrivalDate: it.arrivalDate,
          supplier: it.supplier,
          batchNumber: it.batchNumber,
          notes: it.notes
        })),
        issues: issues.map((iss) => ({
          type: iss.type,
          productName: iss.productName,
          quantity: iss.quantity || 0,
          unit: iss.unit,
          description: iss.description
        }))
      });
      toast.current?.show({
        severity: 'success',
        summary: 'Uspešno',
        detail: 'Otpremnica je poslata na odobravanje',
        life: 2500
      });
      navigate('/magacin/receivings');
    } catch (e: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || e?.message || 'Greška pri kreiranju otpremnice',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const basicSectionHeader = (
    <span className="flex align-items-center gap-2">
      <i className="pi pi-file-edit" />
      <span>Osnovni podaci</span>
      {deliveryNoteName.trim() && <Badge value="✓" severity="success" />}
    </span>
  );

  const itemsSectionHeader = (
    <span className="flex align-items-center gap-2">
      <i className="pi pi-box" />
      <span>Stavke</span>
      <Badge value={items.length} severity={items.length > 0 ? 'success' : 'warning'} />
    </span>
  );

  const issuesSectionHeader = (
    <span className="flex align-items-center gap-2">
      <i className="pi pi-flag" />
      <span>Izveštaji</span>
      {issues.length > 0 && <Badge value={issues.length} severity="warning" />}
    </span>
  );

  return (
    <div className="magacin-page p-3 md:p-4" style={{ paddingBottom: isMobile ? 80 : undefined }}>
      <Toast ref={toast} />

      <div className="magacin-header flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <h2 className="m-0">{isMobile ? 'Nova otpremnica' : 'Nova otpremnica – Ulaz u magacin'}</h2>
          {!isMobile && (
            <div className="text-600 mt-1">
              Unesite naziv otpremnice i dodajte stavke koje su pristigle. Magacioner će ih pregledati i odobriti ili
              odbiti.
            </div>
          )}
        </div>
        <Button
          label={isMobile ? '' : 'Nazad'}
          icon="pi pi-arrow-left"
          severity="secondary"
          onClick={() => navigate('/magacin/receivings')}
          tooltip={isMobile ? 'Nazad' : undefined}
        />
      </div>

      <Accordion
        activeIndex={activeAccordion as any}
        onTabChange={(e) => setActiveAccordion(e.index as any)}
        multiple={!isMobile}
        className="mb-3"
      >
        <AccordionTab header={basicSectionHeader}>
          <div className="p-fluid formgrid grid">
            <div className="field col-12">
              <label>Naziv otpremnice *</label>
              <InputText
                value={deliveryNoteName}
                onChange={(e) => setDeliveryNoteName(e.target.value)}
                placeholder="npr. Otpremnica br. 123/2026"
              />
            </div>
            <div className="field col-12">
              <label>Napomena (opciono)</label>
              <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} autoResize />
            </div>
            <div className="col-12 text-500 text-sm">
              Status nakon slanja: <span className="font-semibold">Na čekanju</span>
            </div>
          </div>
        </AccordionTab>

        <AccordionTab header={itemsSectionHeader}>
          <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-4">
            <label>Kategorija *</label>
            <Dropdown
              value={itemDraft.productTypeId}
              options={categoryOptions}
              onChange={(e) => onCategoryChange(e.value)}
              appendTo="self"
              filter
              placeholder="Odaberite kategoriju"
              showClear
            />
          </div>
          <div className="field col-12 md:col-5">
            <label>Naziv proizvoda *</label>
            <AutoComplete
              value={itemDraft.productName}
              suggestions={productSuggestions}
              completeMethod={completeProduct}
              field="name"
              dropdown
              forceSelection={false}
              placeholder="Unesite naziv ili pretražite postojeće..."
              onChange={(e) => {
                const v: any = e.value;
                const name = typeof v === 'string' ? v : v?.name || '';
                setItemDraft((prev) => ({ ...prev, productName: name }));
              }}
              onSelect={(e) => {
                const p = e.value as MagacinProduct;
                setItemDraft((prev) => ({
                  ...prev,
                  productName: p.name,
                  unit: p.unit || prev.unit
                }));
              }}
            />
          </div>
          <div className="field col-12 md:col-3">
            <label>Datum dolaska *</label>
            <Calendar
              value={itemDraft.arrivalDate}
              onChange={(e) => setItemDraft((prev) => ({ ...prev, arrivalDate: (e.value as Date) || new Date() }))}
              dateFormat="dd.mm.yy"
              showIcon
            />
          </div>
          <div className="field col-6 md:col-3">
            <label>Količina *</label>
            <InputNumber
              value={itemDraft.quantity}
              onValueChange={(e) => setItemDraft((prev) => ({ ...prev, quantity: e.value ?? 0 }))}
              min={0}
              minFractionDigits={0}
              maxFractionDigits={3}
            />
          </div>
          <div className="field col-6 md:col-3">
            <label>Jedinica</label>
            <Dropdown
              value={itemDraft.unit}
              options={unitOptions}
              onChange={(e) => setItemDraft((prev) => ({ ...prev, unit: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-3">
            <label>Dobavljač (opciono)</label>
            <InputText
              value={itemDraft.supplier}
              onChange={(e) => setItemDraft((prev) => ({ ...prev, supplier: e.target.value }))}
              placeholder="Naziv dobavljača"
            />
          </div>
          <div className="field col-12 md:col-3">
            <label>Broj serije (opciono)</label>
            <InputText
              value={itemDraft.batchNumber}
              onChange={(e) => setItemDraft((prev) => ({ ...prev, batchNumber: e.target.value }))}
              placeholder="Broj serije"
            />
          </div>
          <div className="field col-12">
            <label>Napomena za stavku (opciono)</label>
            <InputText
              value={itemDraft.notes}
              onChange={(e) => setItemDraft((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </div>
          <div className="col-12">
            <Button label="Dodaj stavku" icon="pi pi-plus" onClick={addItemToList} className="w-full md:w-auto" />
          </div>
        </div>

        {items.length > 0 && (
          <>
            <h3 className="m-0 mt-3 mb-2">Dodate stavke ({items.length})</h3>
            {isMobile ? (
              <div className="receiving-cards-list">
                {items.map((it, idx) => (
                  <div key={it.key} className="receiving-card">
                    <div className="receiving-card-top">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="receiving-card-number">#{idx + 1} · {it.categoryName}</div>
                        <div className="receiving-card-title">{it.productName}</div>
                      </div>
                      <Button
                        icon="pi pi-trash"
                        severity="danger"
                        rounded
                        text
                        size="small"
                        onClick={() => removeItemFromList(it.key)}
                      />
                    </div>
                    <div className="receiving-card-meta">
                      <span>
                        <strong>{it.quantity} {it.unit}</strong>
                      </span>
                      <span>{fmtDate(it.arrivalDate)}</span>
                      {it.supplier && <span>{it.supplier}</span>}
                      {it.batchNumber && <span>Serija: {it.batchNumber}</span>}
                    </div>
                    {it.notes && <div className="text-600 text-sm mt-1">{it.notes}</div>}
                  </div>
                ))}
              </div>
            ) : (
              <DataTable
                value={items}
                emptyMessage="Nema dodatih stavki"
                className="p-datatable-sm"
                stripedRows
                responsiveLayout="stack"
                breakpoint="960px"
              >
                <Column header="R.br" body={(_: SavedItem, opts: any) => opts.rowIndex + 1} style={{ width: '70px' }} />
                <Column field="categoryName" header="Kategorija" />
                <Column field="productName" header="Proizvod" />
                <Column
                  header="Količina"
                  body={(r: SavedItem) => `${r.quantity} ${r.unit || ''}`.trim()}
                  style={{ width: '130px' }}
                />
                <Column
                  header="Datum"
                  body={(r: SavedItem) => fmtDate(r.arrivalDate)}
                  style={{ width: '120px' }}
                />
                <Column field="supplier" header="Dobavljač" />
                <Column field="batchNumber" header="Br. serije" />
                <Column
                  header=""
                  body={(r: SavedItem) => (
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      rounded
                      text
                      size="small"
                      onClick={() => removeItemFromList(r.key)}
                      tooltip="Obriši stavku"
                    />
                  )}
                  style={{ width: '70px' }}
                />
              </DataTable>
            )}
          </>
        )}
      </AccordionTab>

      <AccordionTab header={issuesSectionHeader}>
        <div className="text-600 mb-2 text-sm">
          Opciono. Dodajte ako je nešto stiglo oštećeno, manjka ili je pogrešno.
        </div>

        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-3">
            <label>Tip *</label>
            <Dropdown
              value={issueDraft.type}
              options={issueTypeOptions}
              onChange={(e) => setIssueDraft((prev) => ({ ...prev, type: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-5">
            <label>Naziv proizvoda</label>
            <InputText
              value={issueDraft.productName}
              onChange={(e) => setIssueDraft((prev) => ({ ...prev, productName: e.target.value }))}
              placeholder="Opciono – ako se odnosi na konkretan proizvod"
            />
          </div>
          <div className="field col-6 md:col-2">
            <label>Količina</label>
            <InputNumber
              value={issueDraft.quantity}
              onValueChange={(e) => setIssueDraft((prev) => ({ ...prev, quantity: e.value ?? 0 }))}
              min={0}
            />
          </div>
          <div className="field col-6 md:col-2">
            <label>Jedinica</label>
            <InputText
              value={issueDraft.unit}
              onChange={(e) => setIssueDraft((prev) => ({ ...prev, unit: e.target.value }))}
              placeholder="kg, kom..."
            />
          </div>
          <div className="field col-12">
            <label>Opis *</label>
            <InputTextarea
              value={issueDraft.description}
              onChange={(e) => setIssueDraft((prev) => ({ ...prev, description: e.target.value }))}
              rows={2}
              autoResize
              placeholder="Detaljno opišite šta je oštećeno / manjka..."
            />
          </div>
          <div className="col-12">
            <Button
              label="Dodaj izveštaj"
              icon="pi pi-flag"
              severity="warning"
              onClick={addIssueToList}
              className="w-full md:w-auto"
            />
          </div>
        </div>

        {issues.length > 0 && (
          <>
            <h3 className="m-0 mt-3 mb-2">Uneti izveštaji ({issues.length})</h3>
            {isMobile ? (
              <div className="receiving-cards-list">
                {issues.map((iss, idx) => (
                  <div key={iss.key} className="receiving-card">
                    <div className="receiving-card-top">
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="receiving-card-number">#{idx + 1}</div>
                        <div className="receiving-card-title">{iss.productName || 'Bez proizvoda'}</div>
                      </div>
                      <Tag value={issueTypeLabel(iss.type)} severity={issueTypeSeverity(iss.type)} />
                    </div>
                    {iss.quantity > 0 && (
                      <div className="receiving-card-meta">
                        <span>
                          <strong>{iss.quantity} {iss.unit}</strong>
                        </span>
                      </div>
                    )}
                    <div className="text-600 text-sm mt-1">{iss.description}</div>
                    <div className="receiving-card-actions">
                      <Button
                        label="Obriši"
                        icon="pi pi-trash"
                        severity="danger"
                        size="small"
                        outlined
                        onClick={() => removeIssueFromList(iss.key)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <DataTable
                value={issues}
                emptyMessage="Nema unetih izveštaja."
                className="p-datatable-sm"
                stripedRows
                responsiveLayout="stack"
                breakpoint="960px"
              >
                <Column header="R.br" body={(_: SavedIssue, opts: any) => opts.rowIndex + 1} style={{ width: '70px' }} />
                <Column
                  header="Tip"
                  body={(r: SavedIssue) => <Tag value={issueTypeLabel(r.type)} severity={issueTypeSeverity(r.type)} />}
                  style={{ width: '140px' }}
                />
                <Column field="productName" header="Proizvod" />
                <Column
                  header="Količina"
                  body={(r: SavedIssue) => (r.quantity ? `${r.quantity} ${r.unit || ''}`.trim() : '-')}
                  style={{ width: '130px' }}
                />
                <Column field="description" header="Opis" />
                <Column
                  header=""
                  body={(r: SavedIssue) => (
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      rounded
                      text
                      size="small"
                      onClick={() => removeIssueFromList(r.key)}
                      tooltip="Obriši izveštaj"
                    />
                  )}
                  style={{ width: '70px' }}
                />
              </DataTable>
            )}
          </>
        )}
      </AccordionTab>
      </Accordion>

      {isMobile ? (
        <div className="magacin-sticky-bar">
          <Button
            label="Otkaži"
            severity="secondary"
            outlined
            onClick={() => navigate('/magacin/receivings')}
          />
          <Button
            label="Pošalji"
            icon="pi pi-send"
            severity="info"
            onClick={submit}
            loading={loading}
            disabled={!canSubmit}
          />
        </div>
      ) : (
        <Card>
          <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
            <div className="text-sm text-600">
              {canSubmit
                ? 'Spremno za slanje. Otpremnica će biti poslata na odobravanje.'
                : 'Da biste poslali otpremnicu, unesite naziv i dodajte bar jednu stavku.'}
            </div>
            <div className="flex gap-2">
              <Button
                label="Otkaži"
                severity="secondary"
                outlined
                onClick={() => navigate('/magacin/receivings')}
              />
              <Button
                label="Pošalji na odobravanje"
                icon="pi pi-send"
                severity="info"
                onClick={submit}
                loading={loading}
                disabled={!canSubmit}
              />
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default MagacinReceivingCreate;
